/**
 * ComplianceSummaryCards - Row of stat cards for the compliance dashboard
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  AlertCircle,
  XCircle,
  PauseCircle,
  BellOff,
  Users,
} from 'lucide-react';
import type { ComplianceSummary } from '@/services/api/ecmReminders';

interface ComplianceSummaryCardsProps {
  summary: ComplianceSummary;
  activeFilter: string | null;
  onFilterClick: (status: string | null) => void;
  /** Count of members with notifications paused or opted out */
  pausedOrOptedOutCount?: number;
}

const CARDS = [
  { key: null, label: 'Total Members', icon: Users, color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-800', border: 'border-gray-200', field: 'total' as const },
  { key: 'completed', label: 'Completed', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200', field: 'completed' as const },
  { key: 'on_track', label: 'On Track', icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200', field: 'on_track' as const },
  { key: 'due', label: 'Due', icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200', field: 'due' as const },
  { key: 'overdue', label: 'Overdue', icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200', field: 'overdue' as const },
  { key: 'critical', label: 'Critical', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200', field: 'critical' as const },
  { key: 'snoozed', label: 'Snoozed', icon: PauseCircle, color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-800', border: 'border-gray-200', field: 'snoozed' as const },
];

export const ComplianceSummaryCards: React.FC<ComplianceSummaryCardsProps> = ({
  summary,
  activeFilter,
  onFilterClick,
  pausedOrOptedOutCount,
}) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
      {CARDS.map((card) => {
        const Icon = card.icon;
        const count = summary[card.field];
        const isActive = activeFilter === card.key;

        return (
          <Card
            key={card.field}
            className={`cursor-pointer transition-all hover:shadow-md ${
              isActive ? `ring-2 ring-offset-1 ring-blue-500 ${card.bg}` : `${card.bg} border ${card.border}`
            }`}
            onClick={() => onFilterClick(isActive ? null : card.key)}
          >
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${card.color}`} />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                  {card.label}
                </span>
              </div>
              <p className={`text-2xl font-bold ${card.color}`}>{count}</p>
            </CardContent>
          </Card>
        );
      })}

      {/* Paused / Opted Out card */}
      {pausedOrOptedOutCount !== undefined && (
        <Card
          className={`cursor-pointer transition-all hover:shadow-md ${
            activeFilter === 'paused_or_opted_out'
              ? 'ring-2 ring-offset-1 ring-blue-500 bg-amber-50 dark:bg-amber-900/20'
              : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200'
          }`}
          onClick={() =>
            onFilterClick(activeFilter === 'paused_or_opted_out' ? null : 'paused_or_opted_out')
          }
        >
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <BellOff className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                Paused / Off
              </span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{pausedOrOptedOutCount}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
